const BRAZIL_STATES = {
  'São Paulo': 'SP',
  'Sao Paulo': 'SP',
  'Rio de Janeiro': 'RJ',
  'Minas Gerais': 'MG',
  'Rio Grande do Sul': 'RS',
  Paraná: 'PR',
  'Santa Catarina': 'SC',
  Bahia: 'BA',
  Ceará: 'CE',
  Pernambuco: 'PE',
  Goiás: 'GO',
  Pará: 'PA',
  Amazonas: 'AM',
  'Espírito Santo': 'ES',
  Maranhão: 'MA',
  Paraíba: 'PB',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  Piauí: 'PI',
  'Rio Grande do Norte': 'RN',
  Alagoas: 'AL',
  Sergipe: 'SE',
  Rondônia: 'RO',
  Tocantins: 'TO',
  Acre: 'AC',
  Amapá: 'AP',
  Roraima: 'RR',
  'Distrito Federal': 'DF'
}

const CACHE_KEY = 'ui-local-location-v2'

const timeFormatter = new Intl.DateTimeFormat([], {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
})

class UiLocalTime extends HTMLElement {
  #timerId = null
  #abortController = new AbortController()
  #location = ''

  connectedCallback() {
    this.#location = this.getInitialLocation()
    this.updateDisplay()
    this.startClock()
    this.fetchClientLocation()
  }

  disconnectedCallback() {
    this.stopClock()
    this.#abortController.abort()
  }

  getInitialLocation() {
    const cached = this.readSessionCache()
    if (cached) return cached
    return this.dataset.defaultLocation || 'São Paulo, SP'
  }

  readSessionCache() {
    try {
      return sessionStorage.getItem(CACHE_KEY) || ''
    } catch {
      return ''
    }
  }

  writeSessionCache(value) {
    try {
      sessionStorage.setItem(CACHE_KEY, value)
    } catch {}
  }

  startClock() {
    this.updateDisplay()
    const msToNextMinute =
      (60 - new Date().getSeconds()) * 1000 - new Date().getMilliseconds()
    this.#timerId = setTimeout(() => {
      this.updateDisplay()
      this.#timerId = setInterval(() => {
        this.updateDisplay()
      }, 60000)
    }, msToNextMinute)
  }

  stopClock() {
    if (this.#timerId === null) {
      return
    }
    clearTimeout(this.#timerId)
    clearInterval(this.#timerId)
    this.#timerId = null
  }

  updateDisplay() {
    const time = timeFormatter.format(new Date())
    const nextContent = `${this.#location} ${time}`
    if (this.textContent !== nextContent) {
      this.textContent = nextContent
    }
  }

  async fetchClientLocation() {
    if (this.readSessionCache()) return

    const fetched = await this.queryGeoLocation()
    if (!fetched) return

    this.#location = fetched
    this.writeSessionCache(fetched)
    this.updateDisplay()
  }

  async queryGeoLocation() {
    const fromIpInfo = await this.fetchIpInfo()
    if (fromIpInfo) return fromIpInfo

    const fromIpWhois = await this.fetchIpWhois()
    if (fromIpWhois) return fromIpWhois

    return null
  }

  createFetchSignal() {
    return AbortSignal.any([
      this.#abortController.signal,
      AbortSignal.timeout(3000)
    ])
  }

  async fetchIpInfo() {
    try {
      const res = await fetch('https://ipinfo.io/json', {
        signal: this.createFetchSignal()
      })
      if (!res.ok) return null
      const data = await res.json()
      return this.formatLocationData(data.city, data.region)
    } catch {
      return null
    }
  }

  async fetchIpWhois() {
    try {
      const res = await fetch('https://ipwho.is/', {
        signal: this.createFetchSignal()
      })
      if (!res.ok) return null
      const data = await res.json()
      if (!data.success) return null
      return this.formatLocationData(data.city, data.region || data.region_code)
    } catch {
      return null
    }
  }

  formatLocationData(city, region) {
    if (!city) return null
    const cleanCity = city === 'Sao Paulo' ? 'São Paulo' : city
    const stateCode = BRAZIL_STATES[region] || region || ''
    return stateCode ? `${cleanCity}, ${stateCode}` : cleanCity
  }
}

if (!customElements.get('local-time')) {
  customElements.define('local-time', UiLocalTime)
}
